import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import {
  addFavoritedUsers,
  addInternalChatMessage,
  bulkDeleteConversations,
  bulkUpdateChatParticipantsAction,
  bulkDeleteUsers,
  bulkUpdateChatMessages,
  bulkUpdateConversation,
  createInternalConversation,
  deleteChatParticipant,
  fetchAllFavoritedUsers,
  fetchAllInternalChats,
  fetchAllUsers,
  internalChatParticipantRemoved,
  markInternalConversationAsRead,
  receiveInternalChatMessage,
  updateInternalChat,
  updateInternalConversation,
  logout,
} from '@store/actions'
import {
  IChatConversation,
  IChatMessage,
  IChatParticipant,
  SendStatus,
} from '@dataTypes/chat.type'
import { logger } from '@utils/safeLogger'
import {
  categorizeConversation,
  sortConversationByDate,
} from '@utils/chat.util'
import { IFavoritedUser } from '@dataTypes/user-favorite.type'
import { getFullName } from '@utils/fullname.util'

const USER_ROLE_LABELS: Record<string, string> = {
  patient: 'Patient',
  provider: 'Provider',
  front_desk_staff: 'Front Desk Staff',
}

function formatUserRole(role: string | undefined): string {
  if (!role) return 'Provider'
  return (
    USER_ROLE_LABELS[role] ??
    role.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  )
}

export type ChatState = {
  data: IChatConversation[]
  inbox: IChatConversation[]
  archived: IChatConversation[]
  favoriteUsers: IFavoritedUser[]
  users: IFavoritedUser[]
  loggedInUser: IFavoritedUser | null
  selectedChatId: string | null
  error: string | null
  loading: boolean
}

const initialState: ChatState = {
  data: [],
  inbox: [],
  archived: [],
  favoriteUsers: [],
  users: [],
  loggedInUser: null,
  selectedChatId: null,
  loading: false,
  error: null,
}

const removeParticipantFromConversations = (
  state: ChatState,
  removedParticipantId: string,
): void => {
  const ownerUserId = state.loggedInUser?.user_id
  const ownerUsername = state.loggedInUser?.username

  const updateAndFilterConversations = (
    conversations: IChatConversation[],
  ): IChatConversation[] =>
    conversations
      .map((conv) => {
        const updatedParticipants = (conv.chat_participant ?? []).filter(
          (p) => p.chat_participant_id !== removedParticipantId,
        )

        if (!ownerUserId && !ownerUsername) {
          return {
            ...conv,
            chat_participant: updatedParticipants,
          }
        }

        const hasOwner = updatedParticipants.some((p) => {
          const participantUserId = p.user_id ?? p.user?.user_id
          const participantUsername = p.username ?? p.user?.username
          return (
            (ownerUserId && participantUserId === ownerUserId) ||
            (ownerUsername && participantUsername === ownerUsername)
          )
        })

        if (!hasOwner) {
          return null
        }

        return {
          ...conv,
          chat_participant: updatedParticipants,
        }
      })
      .filter((conv): conv is IChatConversation => conv !== null)

  state.data = updateAndFilterConversations(state.data)
  state.inbox = updateAndFilterConversations(state.inbox)
  state.archived = updateAndFilterConversations(state.archived)
}

const chatInternalSlice = createSlice({
  name: 'chat-internal',
  initialState,
  reducers: {
    setSelectedChatId: (state, action: PayloadAction<string | null>) => {
      state.selectedChatId = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        fetchAllInternalChats.fulfilled,
        (state, action: PayloadAction<IChatConversation[]>) => {
          state.data = sortConversationByDate(action.payload)
          const { archived, inbox } = categorizeConversation(action.payload)
          state.archived = archived ?? []
          state.inbox = inbox ?? []
          state.loading = false
          state.error = null
        },
      )
      .addCase(fetchAllInternalChats.pending, (state) => {
        state.data = []
        state.loading = true
        state.error = null
      })
      .addCase(fetchAllInternalChats.rejected, (state, action) => {
        logger.error('Fetch internal chats rejected')
        state.error = action.payload || 'Failed to load chats'
        state.loading = false
      })

    builder
      .addCase(fetchAllUsers.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        fetchAllUsers.fulfilled,
        (state, action: PayloadAction<IFavoritedUser[]>) => {
          const loggedInUser = action.payload.find(
            (user) =>
              user.user_id ===
              JSON.parse(localStorage.getItem('user') || '{}').user_id,
          )
          state.loggedInUser = loggedInUser ?? null
          state.users = action.payload
            .filter((user) => user.user_id !== loggedInUser?.user_id)
            .map((user) => ({
              ...user,
              photo: user.provider_profile?.photo ?? undefined,
              username: `${user.provider_profile ? 'Dr. ' : ''}${getFullName(user)}`,
              role:
                user.provider_profile?.work_role ?? formatUserRole(user?.role),
            }))
          state.loading = false
          state.error = null
        },
      )
      .addCase(fetchAllUsers.rejected, (state, action) => {
        state.error = action.payload || 'Failed to fetch all users'
        state.loading = false
      })

    builder
      .addCase(fetchAllFavoritedUsers.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        fetchAllFavoritedUsers.fulfilled,
        (state, action: PayloadAction<IFavoritedUser[]>) => {
          state.favoriteUsers = action.payload.map((user) => ({
            ...user,
            photo: user.provider_profile?.photo ?? undefined,
            username: `${user.provider_profile ? 'Dr. ' : ''}${getFullName(user)}`,
            role:
              user.provider_profile?.work_role ?? formatUserRole(user?.role),
          }))
          state.loading = false
          state.error = null
        },
      )
      .addCase(fetchAllFavoritedUsers.rejected, (state, action) => {
        state.error = action.payload || 'Failed to fetch favorited users'
        state.loading = false
      })

    builder
      .addCase(addFavoritedUsers.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        addFavoritedUsers.fulfilled,
        (state, action: PayloadAction<IFavoritedUser[]>) => {
          state.favoriteUsers = action.payload
          state.loading = false
          state.error = null
        },
      )
      .addCase(addFavoritedUsers.rejected, (state, action) => {
        state.error = action.payload || 'Failed to set favorited users'
        state.loading = false
      })

    builder
      .addCase(bulkDeleteUsers.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(bulkDeleteUsers.fulfilled, (state, action) => {
        const idsToRemove = new Set(action.payload)
        state.favoriteUsers = state.favoriteUsers.filter(
          (u) => !idsToRemove.has(u.user_id),
        )
        state.loading = false
        state.error = null
      })
      .addCase(bulkDeleteUsers.rejected, (state, action) => {
        state.error = action.payload || 'Failed to remove favorited users'
        state.loading = false
      })

    builder
      .addCase(deleteChatParticipant.pending, (state) => {
        state.error = null
      })
      .addCase(
        deleteChatParticipant.fulfilled,
        (state, action: PayloadAction<string>) => {
          removeParticipantFromConversations(state, action.payload)
          state.error = null
        },
      )
      .addCase(deleteChatParticipant.rejected, (state, action) => {
        state.error = action.payload || 'Failed to remove participant from chat'
      })

    builder
      .addCase(bulkUpdateChatParticipantsAction.pending, (state) => {
        state.error = null
      })
      .addCase(
        bulkUpdateChatParticipantsAction.fulfilled,
        (
          state,
          action: PayloadAction<{
            ids: string[]
            data: Partial<IChatParticipant>
          }>,
        ) => {
          state.loading = false
          state.error = null
          if (!state.data) return

          const idSet = new Set(action.payload.ids)
          const updateData = action.payload.data

          const updateParticipantsInConversations = (
            conversations: IChatConversation[],
          ): IChatConversation[] =>
            conversations.map((convo) => {
              const updatedParticipants = (convo.chat_participant ?? []).map(
                (p) =>
                  idSet.has(p.chat_participant_id as string)
                    ? { ...p, ...updateData }
                    : p,
              )
              return { ...convo, chat_participant: updatedParticipants }
            })

          state.data = updateParticipantsInConversations(state.data)
          state.inbox = updateParticipantsInConversations(state.inbox)
          state.archived = updateParticipantsInConversations(state.archived)
        },
      )
      .addCase(bulkUpdateChatParticipantsAction.rejected, (state, action) => {
        state.loading = false
        state.error =
          action.payload || 'Failed to bulk update chat participants'
      })

    builder.addCase(
      internalChatParticipantRemoved,
      (state, action: PayloadAction<{ chat_participant_id: string }>) => {
        removeParticipantFromConversations(
          state,
          action.payload.chat_participant_id,
        )
        state.error = null
      },
    )

    builder
      .addCase(addInternalChatMessage.pending, (state, action) => {
        state.error = null
        const pendingMessage = {
          ...action.meta.arg,
          send_status: SendStatus.queued,
        } as IChatMessage

        const conversations = state.data.map((convo) => {
          if (
            convo.chat_conversation_id === pendingMessage.chat_conversation_id
          ) {
            return {
              ...convo,
              chat_message: [...(convo.chat_message ?? []), pendingMessage],
            }
          }
          return convo
        })

        state.data = sortConversationByDate(conversations)
        const { archived, inbox } = categorizeConversation(conversations)
        state.archived = archived ?? []
        state.inbox = inbox ?? []
      })
      .addCase(
        addInternalChatMessage.fulfilled,
        (state, action: PayloadAction<IChatMessage>) => {
          state.loading = false
          state.error = null
          const message = action.payload

          const conversations = state.data.map((convo) => {
            if (convo.chat_conversation_id !== message.chat_conversation_id) {
              return convo
            }

            const messages = convo.chat_message ?? []
            // Find the queued message to update: same sender + same timestamp, or last queued from this sender
            let indexToUpdate = messages.findIndex(
              (m) =>
                m.sender_id === message.sender_id &&
                m.timestamp === message.timestamp,
            )
            if (indexToUpdate === -1) {
              for (let i = messages.length - 1; i >= 0; i--) {
                const m = messages[i]
                if (
                  m.send_status === SendStatus.queued &&
                  m.sender_id === message.sender_id
                ) {
                  indexToUpdate = i
                  break
                }
              }
            }

            if (indexToUpdate === -1) {
              return {
                ...convo,
                chat_message: [
                  ...messages,
                  { ...message, send_status: SendStatus.sent },
                ],
              }
            }

            const updatedMessages = messages.map((m, i) =>
              i === indexToUpdate
                ? { ...m, ...message, send_status: SendStatus.sent }
                : m,
            )

            return {
              ...convo,
              chat_message: updatedMessages,
            }
          })

          state.data = sortConversationByDate(conversations)
          const { archived, inbox } = categorizeConversation(conversations)
          state.archived = archived ?? []
          state.inbox = inbox ?? []
        },
      )
      .addCase(addInternalChatMessage.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Failed to send message'
      })

    builder
      .addCase(
        bulkUpdateConversation.fulfilled,
        (
          state,
          action: PayloadAction<{
            ids: string[]
            data: Partial<IChatConversation>
          }>,
        ) => {
          state.loading = false
          if (!state.data) return
          state.data = state.data
            .map((convo) => {
              if (
                action.payload.ids.includes(
                  convo.chat_conversation_id as string,
                )
              ) {
                return { ...convo, ...action.payload.data }
              }
              return convo
            })
            .filter((convo) => !convo.is_hidden)
          const { archived, inbox } = categorizeConversation(state.data)
          state.inbox = inbox ?? []
          state.archived = archived ?? []
        },
      )
      .addCase(bulkUpdateConversation.pending, (state) => {
        state.loading = true
      })
      .addCase(bulkUpdateConversation.rejected, (state) => {
        state.error = 'Failed to update bulk conversation'
        state.loading = false
      })

    builder
      .addCase(
        bulkUpdateChatMessages.fulfilled,
        (
          state,
          action: PayloadAction<{
            ids: string[]
            data: Partial<IChatMessage>
          }>,
        ) => {
          state.loading = false
          if (!state.data) return

          const updatedData = state.data.map((convo) => {
            const updatedMessages = (convo.chat_message ?? []).map(
              (message) => {
                if (
                  action.payload.ids.includes(message.chat_message_id as string)
                ) {
                  return { ...message, ...action.payload.data }
                }
                return message
              },
            )

            const hasUpdates = updatedMessages.some(
              (msg, idx) => msg !== (convo.chat_message ?? [])[idx],
            )

            return hasUpdates
              ? { ...convo, chat_message: updatedMessages }
              : convo
          })

          state.data = updatedData

          const { archived, inbox } = categorizeConversation(updatedData)
          state.inbox = inbox ?? []
          state.archived = archived ?? []
        },
      )
      .addCase(bulkUpdateChatMessages.pending, (state) => {
        state.loading = true
      })
      .addCase(bulkUpdateChatMessages.rejected, (state) => {
        state.error = 'Failed to update bulk chat messages'
        state.loading = false
      })

    builder
      .addCase(bulkDeleteConversations.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(bulkDeleteConversations.fulfilled, (state, action) => {
        state.loading = false
        if (!state.data) return
        const removedIds = new Set(action.payload)
        state.data = state.data.filter(
          (convo) => !removedIds.has(convo.chat_conversation_id as string),
        )
        const { archived, inbox } = categorizeConversation(state.data)
        state.archived = archived ?? []
        state.inbox = inbox ?? []
      })
      .addCase(bulkDeleteConversations.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    builder
      .addCase(updateInternalConversation.pending, (state) => {
        state.error = null
        state.loading = false
      })
      .addCase(
        updateInternalConversation.fulfilled,
        (
          state,
          action: PayloadAction<{
            id: string
            data: Partial<IChatConversation>
          }>,
        ) => {
          const updatedData = state.data.map((conv) =>
            conv.chat_conversation_id === action.payload.id
              ? { ...conv, ...action.payload.data }
              : conv,
          )
          state.data = sortConversationByDate(updatedData)

          const { archived, inbox } = categorizeConversation(state.data)
          state.archived = archived ?? []
          state.inbox = inbox ?? []
          state.loading = false
          state.error = null
        },
      )
      .addCase(updateInternalConversation.rejected, (state, action) => {
        state.error = action.payload || 'Failed to update conversation'
      })

    builder
      .addCase(createInternalConversation.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createInternalConversation.fulfilled, (state, action) => {
        const { conversation, participants } = action.payload
        if (conversation && participants) {
          const newConversation = {
            ...conversation,
            chat_participant: participants as IChatParticipant[],
            chat_message: [],
          }
          state.data.push(newConversation)
        }
      })
      .addCase(createInternalConversation.rejected, (state, action) => {
        state.error = action.payload || 'Failed to create conversation'
        state.loading = false
      })

    builder.addCase(receiveInternalChatMessage, (state, action) => {
      const { message } = action.payload
      state.loading = false
      state.error = null
      const conversations = state.data.map((convo) => {
        if (convo.chat_conversation_id === message.chat_conversation_id) {
          return {
            ...convo,
            chat_message: convo.chat_message?.some(
              (m) => m.chat_message_id === message.chat_message_id,
            )
              ? (convo.chat_message ?? [])
              : [...(convo.chat_message ?? []), message],
          }
        }
        return convo
      })

      state.data = sortConversationByDate(conversations)
      const { archived, inbox } = categorizeConversation(conversations)
      state.archived = archived ?? []
      state.inbox = inbox ?? []
    })

    builder.addCase(
      markInternalConversationAsRead.fulfilled,
      (state, action) => {
        const { chat_conversation_id, last_read_at } = action.payload
        const convoIndex = state.data.findIndex(
          (c) => c.chat_conversation_id === chat_conversation_id,
        )
        if (convoIndex !== -1) {
          const convo = state.data[convoIndex]
          const user =
            typeof localStorage !== 'undefined'
              ? JSON.parse(localStorage.getItem('user') || '{}')
              : {}
          const userId = user.user_id
          state.data[convoIndex] = {
            ...convo,
            chat_participant: (convo.chat_participant ?? []).map((p) =>
              p.user_id === userId ? { ...p, last_read_at } : p,
            ),
          }
          const { archived, inbox } = categorizeConversation(state.data)
          state.archived = archived ?? []
          state.inbox = inbox ?? []
        }
      },
    )

    builder
      .addCase(
        updateInternalChat.fulfilled,
        (
          state,
          action: PayloadAction<{
            chat_message_id: string
            data: Partial<IChatMessage>
          }>,
        ) => {
          state.loading = false
          const data = state.data.map((convo) => {
            const messages = convo.chat_message ?? []
            const updatedMessages = messages.map((message) => {
              if (message.chat_message_id === action.payload.chat_message_id) {
                return { ...message, ...action.payload.data }
              }
              return message
            })
            return { ...convo, chat_message: updatedMessages }
          })
          state.data = sortConversationByDate(data)
          const { archived, inbox } = categorizeConversation(data)
          state.archived = archived ?? []
          state.inbox = inbox ?? []
          state.loading = false
          state.error = null
        },
      )
      .addCase(updateInternalChat.pending, (state) => {
        state.error = null
      })
      .addCase(updateInternalChat.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Failed to update chat message'
      })

    builder.addCase(logout.fulfilled, (state) => {
      state.loggedInUser = null
    })
  },
})

export const { setSelectedChatId } = chatInternalSlice.actions

export default chatInternalSlice.reducer
